"use client";

import { useRouter } from "next/navigation";
import {
  type ComponentProps,
  type FocusEvent,
  forwardRef,
  type MouseEvent,
  type PointerEvent,
  type TouchEvent,
  useCallback,
  useRef,
} from "react";
import Link from "@/components/ui/link";
import { useHrefWithFrom } from "@/hooks/use-current-href";
import { isSafeInternalHref } from "@/lib/back-nav";
import { captureCurrentRouteScroll } from "@/lib/navigation-scroll";

type BackAwareLinkProps = Omit<ComponentProps<typeof Link>, "href"> & {
  href: string;
  from?: string | null;
  prefetchOnIntent?: boolean;
};

export const BackAwareLink = forwardRef<HTMLAnchorElement, BackAwareLinkProps>(
  (
    {
      href,
      from,
      prefetch,
      prefetchOnIntent = true,
      onFocus,
      onClick,
      onPointerEnter,
      onTouchStart,
      ...props
    },
    ref,
  ) => {
    const router = useRouter();
    const targetHref = useHrefWithFrom(href, from);
    /**
     * Viewport prefetching is OFF by default, and that is a bug workaround.
     *
     * Reproduced on a production build (`bun run start`) with a cold router
     * cache: twelve programmatic `window.scrollBy` calls — no pointer events
     * at all — produced 2,395 RSC requests, 293 of them to a single URL. The
     * same measurement after the entries land produces zero.
     *
     * The reason it never settles is that the prefetch comes back EMPTY. Next
     * answers `RSC: 1` + `Next-Router-Prefetch: 1` for these routes with a
     * zero-byte body where a navigation gets tens of kilobytes, so nothing
     * satisfiable is ever written to the router's prefetch cache — and every
     * re-evaluation of a link's visibility asks again. Scrolling re-evaluates
     * constantly, so scrolling is a request amplifier. Enough of them in
     * flight and the connection has nothing left for the navigation the user
     * is waiting on, which is the skeleton that never clears.
     *
     * Turning it off costs nothing measurable, because an empty prefetch was
     * never making navigation faster. `prefetchOnIntent` below still prefetches
     * on hover, focus and touchstart — deduped per target, and triggered by
     * the interaction that actually precedes a click — so real intent is still
     * warmed. An explicit `prefetch` prop still wins for any link that wants
     * the old behaviour back.
     */
    const resolvedPrefetch = prefetch ?? false;
    /** Last href this link prefetched, so one interaction prefetches once. */
    const lastPrefetchedRef = useRef<string | null>(null);

    const prefetchTarget = useCallback(() => {
      if (!prefetchOnIntent || !isSafeInternalHref(targetHref)) return;
      // A single tap on a touch device fires `pointerenter` AND `touchstart`
      // (and often `focus` too), so the same target was being prefetched two or
      // three times per tap. Next dedupes within its router cache, but each
      // call still costs a request once the entry goes stale — and
      // `staleTimes.dynamic` is 30s, so a user lingering on a list re-requests
      // every card they brush past.
      if (lastPrefetchedRef.current === targetHref) return;
      lastPrefetchedRef.current = targetHref;
      router.prefetch(targetHref);
    }, [prefetchOnIntent, router, targetHref]);

    const handleFocus = (event: FocusEvent<HTMLAnchorElement>) => {
      prefetchTarget();
      onFocus?.(event);
    };

    const handlePointerEnter = (event: PointerEvent<HTMLAnchorElement>) => {
      prefetchTarget();
      onPointerEnter?.(event);
    };

    const handleTouchStart = (event: TouchEvent<HTMLAnchorElement>) => {
      prefetchTarget();
      onTouchStart?.(event);
    };

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      captureCurrentRouteScroll();
    };

    return (
      <Link
        ref={ref}
        href={targetHref}
        prefetch={resolvedPrefetch}
        onFocus={handleFocus}
        onClick={handleClick}
        onPointerEnter={handlePointerEnter}
        onTouchStart={handleTouchStart}
        {...props}
      />
    );
  },
);

BackAwareLink.displayName = "BackAwareLink";
